import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import type {
  OwnDocumentNotificationStatusQuery,
  OwnDocumentNotificationStatusQueryVariables,
} from '@/modules/users'
import { OWN_DOCUMENT_NOTIFICATION_STATUS_QUERY } from '@/modules/users/queries'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'

interface Props {
  /** Who the document belongs to. Nothing is shown until that is known. */
  ownerId: string | null
  responsibleUserId: string | null
}

/**
 * Tells whoever sets up an expiry reminder whether the document's owner will
 * be notified as well, and if not, what their user type is missing.
 */
export const OwnerNotificationNote: React.FC<Props> = ({
  ownerId,
  responsibleUserId,
}) => {
  const translations = useTranslate({
    notified: ['users.documents.owner-notified', { name: '' }],
    notNotified: [
      'users.documents.owner-not-notified',
      { name: '', viewOwn: '', add: '', change: '', userDocuments: '' },
    ],
    notificationsOff: 'users.documents.notifications-off',

    // The permission table's own labels, so the explanation reads like the
    // table the reader will go and change.
    viewOwn: 'permissions.view-own',
    add: 'permissions.add',
    change: 'permissions.change',
    userDocuments: 'common.user-documents',
  })

  const { data } = useQuery<
    OwnDocumentNotificationStatusQuery,
    OwnDocumentNotificationStatusQueryVariables
  >(OWN_DOCUMENT_NOTIFICATION_STATUS_QUERY, {
    variables: { id: ownerId ?? '' },
    skip: !ownerId,
    fetchPolicy: 'cache-and-network',
  })

  const owner = data?.user
  // A result for a previously picked user is not about this one.
  if (!ownerId || !owner || owner.id !== ownerId) return null

  const status = owner.ownDocumentNotificationStatus
  if (!status) return null

  // The responsible user is notified whatever their permissions.
  if (status !== 'NOTIFICATIONS_OFF' && ownerId === responsibleUserId) {
    return null
  }

  const message =
    status === 'NOTIFICATIONS_OFF'
      ? translations.notificationsOff
      : status === 'RECEIVES'
      ? translations.notified({ name: owner.fullName })
      : translations.notNotified({
          name: owner.fullName,
          viewOwn: translations.viewOwn,
          add: translations.add,
          change: translations.change,
          userDocuments: translations.userDocuments,
        })

  return (
    <p
      className={cn(
        'mt-2 flex gap-2 text-sm leading-snug',
        status === 'RECEIVES' ? 'text-stone-600' : 'text-amber-800'
      )}
    >
      <FAIcon icon="info-circle" className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </p>
  )
}
